<template>
  <div>
    <!-- BVN Check Dialog -->
    <v-dialog v-model="dialogOpen" max-width="500" persistent>
      <v-card v-if="verificationStep === 0">
        <v-card-title class="justify-end">
          <v-btn icon small @click="closeModal"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div>
            <v-icon color="orange">mdi-information</v-icon>
          </div>
          <div class="text-lg font-semibold text-black mt-4">
            Complete Tier 2 KYC - BVN Verification
          </div>
          <div class="text-gray-600 mt-2">
            {{ tier2Message }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" @click="startVerification">Verify BVN Now</v-btn>
          <v-btn text @click="cancelVerification">Cancel</v-btn>
        </v-card-actions>
      </v-card>

      <v-card v-if="verificationStep === 1">
        <v-card-title class="justify-end">
          <v-btn icon small :disabled="isVerifying" @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="text-lg font-semibold text-black mt-4 mb-4">
            Enter Your BVN
          </div>
          <div class="text-gray-600 mt-2 mb-4">
            Please enter your 11-digit Bank Verification Number (BVN) to continue with Tier 2 KYC verification.
          </div>

          <!-- BVN Input -->
          <v-text-field
            v-model="bvn"
            label="BVN"
            placeholder="Enter 11-digit BVN"
            :rules="bvnRules"
            :error-messages="bvnError"
            :disabled="isVerifying"
            outlined
            dense
            type="tel"
            maxlength="11"
            @input="validateBvn"
            @keyup.enter="verifyBvn"
          ></v-text-field>

          <!-- Error Message -->
          <div v-if="bvnError" class="text-red-600 text-sm mt-2">
            {{ bvnError }}
          </div>

          <!-- The BVN itself is fine here - it's the profile name that doesn't
               match it, and that can only be fixed on the profile tab. -->
          <div v-if="isNameMismatch" class="text-sm mt-2">
            <router-link to="/user/settings?tab=0" @click.native="closeModal">
              Go to profile to update your full name
            </router-link>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            @click="verifyBvn"
            :loading="isVerifying"
            :disabled="!isBvnValid || isVerifying"
          >
            Verify BVN
          </v-btn>
          <v-btn text @click="goBack" :disabled="isVerifying">Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import kycApi from '../api/users/kyc'
import { updateProfile } from '../utils/helpers'

export default {
  name: 'BvnCheckModal',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    tier2Message: {
      type: String,
      default: 'You need to complete BVN verification to proceed with Tier 2 KYC verification.'
    }
  },
  data() {
    return {
      verificationStep: 0,
      bvn: '',
      bvnError: '',
      isVerifying: false
    }
  },
  computed: {
    dialogOpen: {
      get() {
        return this.open
      },
      set(value) {
        if (!value) {
          this.$emit('close')
        }
      }
    },
    isNameMismatch() {
      return /name/i.test(this.bvnError) && /date of birth|dob/i.test(this.bvnError)
    },
    isBvnValid() {
      return this.bvn.length === 11 && /^\d+$/.test(this.bvn)
    },
    bvnRules() {
      return [
        (v) => !!v || 'BVN is required',
        (v) => (v && v.length === 11) || 'BVN must be exactly 11 digits',
        (v) => (v && /^\d+$/.test(v)) || 'BVN must contain only numbers'
      ]
    }
  },
  watch: {
    open(newVal) {
      if (newVal) {
        this.resetVerification()
      }
    }
  },
  methods: {
    startVerification() {
      this.verificationStep = 1
    },

    goBack() {
      this.verificationStep = 0
      this.bvn = ''
      this.bvnError = ''
    },

    validateBvn() {
      this.bvnError = ''
      if (this.bvn && this.bvn.length > 0) {
        // Remove any non-numeric characters
        this.bvn = this.bvn.replace(/\D/g, '')

        if (this.bvn.length !== 11) {
          this.bvnError = 'BVN must be exactly 11 digits'
        } else if (!/^\d+$/.test(this.bvn)) {
          this.bvnError = 'BVN must contain only numbers'
        }
      }
    },

    async verifyBvn() {
      // Validate BVN before submitting
      if (!this.isBvnValid) {
        this.bvnError = 'Please enter a valid 11-digit BVN'
        return
      }

      this.isVerifying = true
      this.bvnError = ''

      try {
        const result = await kycApi.kyc().bvnCheck({ bvn: this.bvn })

        if (result.error) {
          this.$store.dispatch('alert', {
            message:
              result.errorMessage?.message ||
              result.message ||
              'Failed to verify BVN. Please try again.',
            error: true,
            status: true
          })
          this.bvnError = result.errorMessage?.message || 'BVN verification failed'
          this.isVerifying = false
          return
        }

        // Success - update profile and emit success event
        this.$store.dispatch('alert', {
          message: 'BVN verified successfully!',
          error: false,
          status: true
        })

        console.log('result', result)

        // Update user profile with new KYC data
        if (result.data) {
          updateProfile({ profile: result.data })
        }

        // Reset verification state
        this.resetVerification()

        // Close the modal immediately
        this.$emit('close')

        // Emit success event to parent
        this.$emit('verification-success', result.data)
      } catch (error) {
        console.error('BVN verification error:', error)
        this.$store.dispatch('alert', {
          message: 'An error occurred while verifying BVN. Please try again.',
          error: true,
          status: true
        })
        this.bvnError = 'An error occurred. Please try again.'
      } finally {
        this.isVerifying = false
      }
    },

    cancelVerification() {
      this.$emit('verification-cancelled')
      this.closeModal()
    },

    closeModal() {
      this.$emit('close')
    },

    resetVerification() {
      this.verificationStep = 0
      this.bvn = ''
      this.bvnError = ''
      this.isVerifying = false
    }
  }
}
</script>

<style scoped>
/* Custom styles for the verification dialog */
.v-dialog .v-card {
  border-radius: 12px;
}

.v-card-text {
  padding: 24px;
}

.v-card-actions {
  padding: 16px 24px 24px;
  gap: 8px;
}
</style>

